/**
 * Keep a list's scroll position when the dealer opens a product and comes
 * back (back button / Android back / browser back).
 *
 * Screens unmount on navigation, so the position lives in a module-level map
 * keyed per list (e.g. `home:<category>`, `saved`). On return the list
 * re-mounts and its data may arrive a moment later (realtime listeners), so
 * restoring waits until `ready` and until the content is tall enough to reach
 * the saved offset — giving up after a short timeout and using what fits.
 *
 * The tapped product is remembered too (`rememberAnchor`). If, after
 * restoring, that product is not in view (the list changed while the dealer
 * was away), it is scrolled to the centre instead.
 *
 * Never fights the user: any touch / wheel / pointer input on the list before
 * restoring finishes cancels the restore.
 *
 * Works whether the list's own element scrolls or the page does (the element
 * just grows with its content).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

interface SavedPosition {
  top: number;
  /** Id of the item the dealer tapped to leave the list, if any. */
  anchorId: string | null;
}

const positions = new Map<string, SavedPosition>();

/** Wrap each list item in an element carrying this attribute = the item id. */
export const SCROLL_ANCHOR_ATTR = 'data-scroll-anchor';

/** Stop waiting for the content to grow after this long; restore what fits. */
const RESTORE_TIMEOUT_MS = 2500;

export interface ScrollRestoreOptions {
  /**
   * Bring an item into view when it isn't in the DOM (virtualized lists).
   * Return true when handled.
   */
  scrollToAnchor?: (id: string) => boolean;
}

/** The element actually scrolling: the list itself, or else the page. */
function scrollerFor(el: HTMLElement): HTMLElement {
  if (el.scrollHeight > el.clientHeight + 1) return el;
  const doc = document.scrollingElement as HTMLElement | null;
  if (doc && doc.scrollHeight > doc.clientHeight + 1) return doc;
  return el;
}

function findAnchor(el: HTMLElement, id: string): HTMLElement | null {
  const value = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id.replace(/"/g, '\\"');
  return el.querySelector<HTMLElement>(`[${SCROLL_ANCHOR_ATTR}="${value}"]`);
}

/** Is the anchor's centre inside the visible part of the scroller? */
function anchorInView(scroller: HTMLElement, anchor: HTMLElement): boolean {
  const isPage = scroller === document.scrollingElement;
  const view = isPage
    ? { top: 0, bottom: window.innerHeight }
    : scroller.getBoundingClientRect();
  const r = anchor.getBoundingClientRect();
  const mid = (r.top + r.bottom) / 2;
  return mid >= view.top && mid <= view.bottom;
}

/**
 * @param key   identifies the list; `undefined` disables the hook.
 * @param ref   the list's scroll container (or the element that grows with it).
 * @param ready true once the list has rendered its items.
 * @returns `rememberAnchor(id)` — call it when an item is tapped to leave.
 */
export function useScrollRestore(
  key: string | undefined,
  ref: RefObject<HTMLElement>,
  ready: boolean,
  options: ScrollRestoreOptions = {},
): (anchorId: string) => void {
  const keyRef = useRef(key);
  /** Restoring is finished (or cancelled) — scroll events are saved from now. */
  const doneRef = useRef(false);
  /** Leaving via a tap: ignore the scroll events unmounting may cause. */
  const frozenRef = useRef(false);
  const timedOutRef = useRef(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // A new list (first mount or category switch) starts a fresh restore.
  useLayoutEffect(() => {
    keyRef.current = key;
    doneRef.current = false;
    frozenRef.current = false;
    timedOutRef.current = false;
  }, [key]);

  const attempt = useCallback((): void => {
    const el = ref.current;
    const k = keyRef.current;
    if (doneRef.current || !el || !k) return;
    const saved = positions.get(k);
    const scroller = scrollerFor(el);

    if (!saved) {
      // Nothing remembered for this list: start it at the top.
      if (scroller === el) el.scrollTop = 0;
      doneRef.current = true;
      return;
    }

    const max = scroller.scrollHeight - scroller.clientHeight;
    if (max < saved.top - 1 && !timedOutRef.current) return; // wait for content
    scroller.scrollTop = Math.min(saved.top, Math.max(0, max));
    doneRef.current = true;

    const anchorId = saved.anchorId;
    if (!anchorId) return;
    // Check after the list has re-rendered at the new offset (virtualized
    // lists render their rows a frame later).
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const node = ref.current;
        if (!node || keyRef.current !== k) return;
        const anchor = findAnchor(node, anchorId);
        if (anchor && anchorInView(scrollerFor(node), anchor)) return;
        if (anchor) anchor.scrollIntoView({ block: 'center' });
        else optionsRef.current.scrollToAnchor?.(anchorId);
      }),
    );
  }, [ref]);

  // Restore once ready; retry as the content grows (and on every re-render,
  // e.g. realtime data arriving); stop waiting after a timeout. Layout effects
  // so a cached list is restored before first paint.
  useLayoutEffect(() => {
    if (!key || !ready) return;
    attempt();
    if (doneRef.current) return;
    const el = ref.current;
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => attempt()) : null;
    if (el && ro) {
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    const timer = window.setTimeout(() => {
      timedOutRef.current = true;
      attempt();
    }, RESTORE_TIMEOUT_MS);
    return () => {
      ro?.disconnect();
      window.clearTimeout(timer);
    };
  }, [key, ready, attempt, ref]);

  useLayoutEffect(() => {
    if (key && ready) attempt();
  });

  // Save on scroll; cancel a pending restore on user input.
  useEffect(() => {
    const el = ref.current;
    if (!key || !el) return;
    const onScroll = (): void => {
      const k = keyRef.current;
      if (!doneRef.current || frozenRef.current || !k || !el.isConnected) return;
      // A dealer scroll means the old tap no longer says where they are.
      positions.set(k, { top: scrollerFor(el).scrollTop, anchorId: null });
    };
    const onInput = (): void => {
      doneRef.current = true;
      // Back on this list (it may have stayed mounted under a product page):
      // the dealer is scrolling it again, so resume saving the position.
      frozenRef.current = false;
    };
    const inputs = ['touchstart', 'wheel', 'pointerdown', 'keydown'] as const;
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    for (const t of inputs) el.addEventListener(t, onInput, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
      for (const t of inputs) el.removeEventListener(t, onInput);
    };
  }, [key, ready, ref]);

  return useCallback(
    (anchorId: string): void => {
      const el = ref.current;
      const k = keyRef.current;
      if (!el || !k) return;
      positions.set(k, { top: scrollerFor(el).scrollTop, anchorId });
      frozenRef.current = true;
    },
    [ref],
  );
}
